import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faNewspaper,
  faSpinner,
  faArrowUpRightFromSquare,
  faRotateRight,
} from '@fortawesome/free-solid-svg-icons';
import { newsApi } from '../api/newsApi';
import { useBookSymbols } from '../hooks/useBookSymbols';
import { useQuote } from '../contexts/QuoteContext';

const NewsFeed = () => {
  const { symbols: bookSymbols, loading: bookLoading } = useBookSymbols();
  const { selectSymbol } = useQuote();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchNews = async (forceRefresh = false) => {
    if (!bookSymbols.length) {
      setArticles([]);
      setError(null);
      return;
    }

    const symbolsParam = bookSymbols.slice(0, 50).join(',');

    try {
      setLoading(true);
      setError(null);

      let articlesOut = [];

      try {
        const dbResponse = await newsApi.getNews({
          limit: 30,
          offset: 0,
          symbols: symbolsParam,
        });
        articlesOut = dbResponse.data || [];
      } catch (dbErr) {
        console.warn('Book news DB fetch failed:', dbErr);
      }

      if (forceRefresh || articlesOut.length === 0) {
        try {
          const fresh = await newsApi.fetchNews({
            limit: 30,
            symbols: symbolsParam,
            sort: 'desc',
          });
          if (fresh.data?.length) {
            articlesOut = fresh.data;
          }
        } catch (fetchErr) {
          console.warn('Book news Alpaca fetch failed:', fetchErr);
          if (!articlesOut.length) throw fetchErr;
        }
      }

      setArticles(articlesOut);
    } catch (err) {
      console.error('Error loading book news:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load news');
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (bookLoading) return;
    fetchNews(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookLoading, bookSymbols.join(',')]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="lt-card overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-hairline">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faNewspaper} className="text-xs text-muted" />
            <span className="lt-label">Book news</span>
          </div>
          <div className="text-[11px] text-muted mt-0.5 truncate">
            Positions + watchlists
            {bookSymbols.length ? ` · ${bookSymbols.length} symbols` : ''}
          </div>
        </div>
        <button
          type="button"
          onClick={() => fetchNews(true)}
          className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-hairline text-muted hover:bg-elevated hover:text-ink disabled:opacity-50"
          disabled={loading || bookLoading || !bookSymbols.length}
          title="Refresh"
        >
          <FontAwesomeIcon
            icon={loading || bookLoading ? faSpinner : faRotateRight}
            className={`text-xs ${loading || bookLoading ? 'animate-spin' : ''}`}
          />
        </button>
      </div>

      {error && (
        <div className="mx-3 mt-3 px-2.5 py-2 rounded-md text-xs bg-[var(--lt-bear-dim)] text-bear border border-[rgba(240,113,103,0.28)]">
          {error}
        </div>
      )}

      <div className="max-h-[360px] overflow-y-auto">
        {bookLoading || (loading && articles.length === 0) ? (
          <div className="px-3 py-8 text-center text-xs text-muted">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
            Loading book news...
          </div>
        ) : !bookSymbols.length ? (
          <div className="px-3 py-8 text-center text-xs text-muted">
            Add positions or watchlist symbols to see filtered news.
          </div>
        ) : articles.length === 0 ? (
          <div className="px-3 py-8 text-center text-xs text-muted">
            No recent articles for your book.
          </div>
        ) : (
          <div className="divide-y divide-hairline">
            {articles.map((article, idx) => (
              <div
                key={article.id || article.alpacaId || `${article.url}-${idx}`}
                className="px-3 py-2.5 hover:bg-elevated transition-colors"
              >
                <a
                  href={article.url}
                  target="_blank"
                  rel="noreferrer"
                  className="block"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-ink text-xs line-clamp-2">
                        {article.headline || article.title}
                      </div>
                      <div className="flex items-center gap-2 text-[10px] text-muted mt-0.5">
                        {article.source && <span className="truncate">{article.source}</span>}
                        {(article.createdAt || article.publishedAt) && (
                          <span>{formatDate(article.createdAt || article.publishedAt)}</span>
                        )}
                      </div>
                    </div>
                    <FontAwesomeIcon
                      icon={faArrowUpRightFromSquare}
                      className="text-muted text-[10px] flex-shrink-0 mt-0.5"
                    />
                  </div>
                </a>
                {article.symbols?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {article.symbols
                      .filter((s) => bookSymbols.includes(String(s).toUpperCase()))
                      .slice(0, 4)
                      .map((symbol) => (
                        <button
                          key={symbol}
                          type="button"
                          onClick={() => selectSymbol(symbol)}
                          className="px-1.5 py-0.5 rounded text-[9px] font-medium border border-hairline text-action hover:bg-elevated"
                        >
                          {symbol}
                        </button>
                      ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsFeed;
